import {
  PREVIEW_LINK_PROTOCOLS,
  PREVIEW_MESSAGE_SOURCE,
  PREVIEW_MODULE_ID_ATTRIBUTE,
  PREVIEW_PHONE_DROPDOWN_SELECTOR,
} from "../config";

/**
 * Runs inside the iframe: posts module rects + height to the parent (nonce-authenticated) and controls link
 * clicks. With `allowLinks` a real user click on a web/mailto/tel link opens a new tab — this is independent
 * of whether author scripts run, so links stay live with JavaScript disabled. Every other click (in-frame
 * nav, other schemes, and all clicks in comment mode) is blocked.
 */
export const buildMeasurementScript = (nonce: string, allowLinks: boolean): string => `
(function () {
  var NONCE = ${JSON.stringify(nonce)};
  var SOURCE = ${JSON.stringify(PREVIEW_MESSAGE_SOURCE)};
  var SELECTOR = "[${PREVIEW_MODULE_ID_ATTRIBUTE}]";
  var ALLOW_LINKS = ${allowLinks};
  var LINK_PROTOCOLS = ${JSON.stringify(PREVIEW_LINK_PROTOCOLS)};
  var PHONE_DROPDOWN_SELECTOR = ${JSON.stringify(PREVIEW_PHONE_DROPDOWN_SELECTOR)};

  function post(message) {
    try {
      message.source = SOURCE;
      message.nonce = NONCE;
      window.parent.postMessage(message, "*");
    } catch (error) {}
  }

  // LP modules are often matched on a zero-size wrapper; fall back to the union of descendant boxes.
  function boundsOf(node) {
    var rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) { return rect; }
    var kids = node.getElementsByTagName("*");
    var top = Infinity, left = Infinity, bottom = -Infinity, right = -Infinity, found = false;
    for (var k = 0; k < kids.length; k++) {
      var kr = kids[k].getBoundingClientRect();
      if (kr.width === 0 && kr.height === 0) { continue; }
      found = true;
      if (kr.top < top) { top = kr.top; }
      if (kr.left < left) { left = kr.left; }
      if (kr.bottom > bottom) { bottom = kr.bottom; }
      if (kr.right > right) { right = kr.right; }
    }
    if (!found) { return rect; }
    return { top: top, left: left, width: right - left, height: bottom - top };
  }

  function measure() {
    var nodes = document.querySelectorAll(SELECTOR);
    var modules = [];
    for (var i = 0; i < nodes.length; i++) {
      var rect = boundsOf(nodes[i]);
      modules.push({
        id: nodes[i].getAttribute("${PREVIEW_MODULE_ID_ATTRIBUTE}"),
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });
    }
    var body = document.body;
    var html = document.documentElement;
    // Height = furthest descendant bottom, NOT scrollHeight/offsetHeight. Content often sets
    // html/body { height:100% } (BEE landing pages), which pins scrollHeight to the iframe element's
    // CURRENT height — so a Math.max with it can only grow and never shrink back (e.g. after a quiz
    // hides rows on return to preview). body's descendants exclude body itself, so this stays the true
    // content extent; scrollHeight is only a fallback when there is nothing measurable to bound.
    // The phone field's country list is left out of it and handled by sizeForPhoneDropdown.
    var maxBottom = 0;
    if (body) {
      var walker = document.createTreeWalker(body, NodeFilter.SHOW_ELEMENT, {
        acceptNode: function (node) {
          return node.matches(PHONE_DROPDOWN_SELECTOR) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        }
      });
      for (var node = walker.nextNode(); node; node = walker.nextNode()) {
        var bottom = node.getBoundingClientRect().bottom + window.scrollY;
        if (bottom > maxBottom) { maxBottom = bottom; }
      }
    }
    // No padding on top of the measurement: the reported height becomes the iframe's own height, so any
    // constant buffer loses the fixed point and creeps upward 1px per frame whenever a descendant's bottom
    // is viewport-locked (min-height:100vh, height:100% under html/body{height:100%}).
    var height = Math.ceil(
      maxBottom > 0 ? maxBottom : Math.max(html.scrollHeight, body ? body.scrollHeight : 0)
    );
    post({ type: "modules", height: sizeForPhoneDropdown(height), modules: modules });
  }

  // An open country list may run past the content's end (it opens downward whenever it has a search box),
  // so the frame grows to show the list's own box — never its rows' boxes, which report themselves
  // unclipped thousands of px below its scroll area. The height is taken from before the list opened and
  // restored once it closes: viewport-locked content follows the grown frame, so measuring it again would
  // only ever confirm the grown height.
  var heightBeforePhoneDropdown = null;
  function sizeForPhoneDropdown(contentHeight) {
    var dropdownBottom = 0;
    var dropdowns = document.querySelectorAll(PHONE_DROPDOWN_SELECTOR);
    for (var d = 0; d < dropdowns.length; d++) {
      var box = dropdowns[d].getBoundingClientRect();
      if (box.height > 0 && box.bottom + window.scrollY > dropdownBottom) {
        dropdownBottom = box.bottom + window.scrollY;
      }
    }
    if (dropdownBottom > 0) {
      if (heightBeforePhoneDropdown === null) { heightBeforePhoneDropdown = contentHeight; }
      return Math.max(heightBeforePhoneDropdown, Math.ceil(dropdownBottom));
    }
    if (heightBeforePhoneDropdown !== null) {
      contentHeight = heightBeforePhoneDropdown;
      heightBeforePhoneDropdown = null;
    }
    return contentHeight;
  }

  var rafId = null;
  function scheduleMeasure() {
    if (rafId !== null) { return; }
    if (window.requestAnimationFrame) {
      rafId = window.requestAnimationFrame(function () { rafId = null; measure(); });
    } else {
      rafId = setTimeout(function () { rafId = null; measure(); }, 16);
    }
  }

  document.addEventListener("click", function (event) {
    var node = event.target;
    var anchor = node && node.closest ? node.closest("a") : null;
    if (!anchor) { return; }
    var raw = anchor.getAttribute("href") || "";
    // A BEE button that only drives quiz JS has href="#", but the backend appends tracking params to every
    // href (TouchpointService#appendUrlParamsToHref), so it arrives as "?utm_…#". The live page's own query is
    // identical, making the click a same-document no-op there; here it would open a stray tab.
    var isInPage = raw.charAt(0) === "#" || raw.charAt(0) === "?";
    // Real user clicks on real web/mailto/tel links open a NEW tab (uses the click's own user activation, so
    // no popup block); noopener+noreferrer so the opened page can't reach the portal. Everything else is
    // blocked: in-frame nav, other schemes, in-page links (quiz JS still runs when enabled —
    // preventDefault keeps propagation), and ALL clicks in comment mode (ALLOW_LINKS false → annotation only).
    if (ALLOW_LINKS && event.isTrusted && raw && !isInPage && LINK_PROTOCOLS.indexOf(anchor.protocol) !== -1) {
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      return;
    }
    event.preventDefault();
  }, true);

  window.addEventListener("resize", scheduleMeasure);
  window.addEventListener("scroll", scheduleMeasure, true);
  window.addEventListener("load", measure);

  if (typeof ResizeObserver !== "undefined" && document.body) {
    new ResizeObserver(scheduleMeasure).observe(document.body);
  }

  // Reposition pins when the DOM changes. Rects carry the stable id, so a moved node keeps its mapping.
  if (typeof MutationObserver !== "undefined" && document.body) {
    new MutationObserver(scheduleMeasure).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });
  }

  function init() {
    post({ type: "ready" });
    measure();
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(measure);
    }
    // Re-measure after late image/layout settling.
    setTimeout(measure, 300);
    setTimeout(measure, 1000);
  }

  if (document.readyState === "interactive" || document.readyState === "complete") {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init);
  }
})();
`;

/**
 * In-memory storage/cookie shim (T3). On the opaque origin, `localStorage`/`sessionStorage` access throws
 * `SecurityError` — enough to halt an author script. Injected before author scripts, only when `executeScripts`.
 */
export const buildStorageShimScript = (): string => `
(function () {
  function memStorage() {
    var store = {};
    return {
      getItem: function (k) { return Object.prototype.hasOwnProperty.call(store, k) ? store[k] : null; },
      setItem: function (k, v) { store[k] = String(v); },
      removeItem: function (k) { delete store[k]; },
      clear: function () { store = {}; },
      key: function (i) { var keys = Object.keys(store); return i in keys ? keys[i] : null; },
      get length() { return Object.keys(store).length; }
    };
  }
  function shim(name) {
    try { void window[name]; return; } catch (probeError) {}
    try { Object.defineProperty(window, name, { configurable: true, value: memStorage() }); } catch (defineError) {}
  }
  shim("localStorage");
  shim("sessionStorage");
  try {
    var jar = "";
    Object.defineProperty(document, "cookie", {
      configurable: true,
      get: function () { return jar; },
      set: function (value) { jar = String(value); }
    });
  } catch (cookieError) {}
})();
`;

/**
 * Draws the country-flag dropdown on the phone field. On the live page that is done by `main.js`, which the
 * preview strips along with the rest of the backend runtime (`BACKEND_RUNTIME_SCRIPT_MARKERS`) because it
 * only errors on the opaque origin — leaving the field visibly different from the published page. This loads
 * just the library, from the URL resolved off the stylesheet link the backend already wrote.
 *
 * Appearance only: `formatAsYouType` and `strictMode` (which the live page passes) need `utils.js` via a
 * cross-origin dynamic `import()` the sandbox cannot be relied on to resolve, and nothing here validates or
 * submits. Enhances the FIRST tel input, matching `handlePhoneInput()` on the live page.
 *
 * Without `allowDropdown` (comment mode) the country list must not open, but the library's own
 * `allowDropdown: false` is not the way: its stylesheet anchors the flag to the left only under
 * `.iti--allow-dropdown`, so the flag jumps to the right edge and loses its arrow. The dropdown stays
 * configured as on the live page and everything the library put beside the input is made `inert` — no
 * click, no focus, no keyboard opening.
 */
export const buildPhoneFieldScript = (libraryUrl: string, allowDropdown: boolean): string => `
(function () {
  var ALLOW_DROPDOWN = ${allowDropdown};
  var input = document.querySelector("input[type='tel']");
  if (!input) { return; }

  function lockDropdown() {
    var wrapper = input.parentNode;
    if (!wrapper) { return; }
    for (var i = 0; i < wrapper.children.length; i++) {
      if (wrapper.children[i] !== input) { wrapper.children[i].setAttribute("inert", ""); }
    }
  }

  var script = document.createElement("script");
  script.src = ${JSON.stringify(libraryUrl)};
  script.onload = function () {
    if (typeof window.intlTelInput !== "function") { return; }
    try {
      window.intlTelInput(input, { autoPlaceholder: "off", initialCountry: "us" });
      if (!ALLOW_DROPDOWN) { lockDropdown(); }
    } catch (initError) {}
  };
  document.head.appendChild(script);
})();
`;

/**
 * Navigation/history shim (T3). `history.pushState`/`replaceState` throw `SecurityError` on the opaque origin;
 * `location.assign`/`replace`/`window.open` could navigate the preview away. Overrides are best-effort
 * (try/catch) — the reliable part is swallowing the `history.*` throw-halt.
 */
export const buildNavigationShimScript = (): string => `
(function () {
  function noop() {}
  try {
    var h = window.history;
    if (h) {
      ["pushState", "replaceState"].forEach(function (name) {
        var native = h[name];
        try {
          h[name] = function () {
            try { return native.apply(h, arguments); } catch (applyError) {}
          };
        } catch (assignError) {}
      });
    }
  } catch (historyError) {}
  try { window.location.assign = noop; } catch (assignError) {}
  try { window.location.replace = noop; } catch (replaceError) {}
  try { window.open = function () { return null; }; } catch (openError) {}
})();
`;
